import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  Building2,
  Calendar,
  Wallet,
  Users,
  FileText,
  Bell,
  ArrowRight,
  TrendingUp,
  Clock,
  Sparkles,
  Receipt,
} from 'lucide-react';
import { useDashboardStats } from '../../application/hooks/useDashboard';
import { useCurrentUser } from '../../application/hooks/useCurrentUser';
import { useInstallments, useMySubscription } from '../../application/hooks/useSubscription';
import { useTranslation } from '../../application/hooks/useTranslation';
import { formatDateTime, formatNumber } from '../../domain/formatters/date';
import { formatBDT } from '../../domain/formatters/currency';
import { resolvePrepaid } from '../../domain/calculators/installments';
import { resolveWallet } from '../../domain/calculators/processWithdrawal';
import { TIER_DESIGNATION_MAP, TierLevel, TIER_RANK_MAP } from '../../domain/enums/TierLevel';
import { DelinquencyStatus } from '../../domain/enums/SystemEnums';
import {
  TENOR,
  calculateTotalPaid,
  getValidInstallmentRows,
  isInstallmentCleared,
} from '../../domain/calculators/installments';
import { QueryState } from '../components/feedback/QueryState';
import { AmountDisplay } from '../components/ui/AmountDisplay';
import { Badge, type BadgeTone } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import './pages.css';
import './Dashboard.css';
import { BKASH_ENABLED } from '../../config/features';
import { PayLink } from '../components/payments/PayLink';
import { ProcessWithdrawalModal } from '../components/commissions/ProcessWithdrawalModal';

const STATUS_TONE: Record<string, BadgeTone> = {
  PAID: 'success',
  ADVANCED: 'info',
  UPCOMING: 'neutral',
  DUE: 'warning',
  IN_NOTICE_PERIOD: 'warning',
  DEFAULTED: 'danger',
};

function formatChannelName(channel: string) {
  switch (channel) {
    case 'BKASH':
      return { label: 'bKash', cls: 'bkash' };
    case 'BANK_SLIP':
    case 'BANK_EFT':
      return { label: 'Bank Slip', cls: 'bank' };
    case 'CASH_COUNTER':
      // Legacy row only: neutral label, never offered anywhere.
      return { label: 'Other', cls: 'bank' };
    case 'COMMISSION_WALLET':
      return { label: 'Commission Wallet', cls: 'commission' };
    default:
      return { label: channel, cls: 'bank' };
  }
}

/**
 * World-Class Sristi Green Valley Ltd Dashboard (E20)
 * Streamlined, professional, non-redundant executive layout.
 * Each widget owns its canonical action without floating duplicates.
 */
export default function DashboardPage() {
  const { t, locale } = useTranslation();
  const { user } = useCurrentUser();
  const stats = useDashboardStats();
  const sub = useMySubscription();
  const rows = useInstallments(sub.data?.id);
  const [processOpen, setProcessOpen] = useState(false);

  const userTier = user?.tier ?? TierLevel.L1;
  const userRank = TIER_RANK_MAP[userTier] ?? 1;
  const isAgent = userRank >= 2;
  const isDirector = userRank >= 6;

  // Real estate financial metrics
  const validRows = useMemo(() => getValidInstallmentRows(rows.data), [rows.data]);
  const rowClearedCount = useMemo(
    () => validRows.filter((r) => isInstallmentCleared(r.status)).length,
    [validRows],
  );
  const clearedCount = stats.data?.clearedInstallments ?? sub.data?.clearedInstallments ?? rowClearedCount;
  const totalCount = TENOR;
  const rawMonthly = stats.data?.monthlyAmount ?? sub.data?.monthlyAmount;
  const hasMonthlyAmount = typeof rawMonthly === 'number';
  const monthlyAmount = rawMonthly ?? 0;
  const totalCapitalPaid = calculateTotalPaid(sub.data, rows.data);
  const progressPercent = Math.min(100, Math.round((clearedCount / TENOR) * 100));

  // Consistent commitment/paid bases (exclude booking from remaining balance):
  // totalInstallmentCommitment tracks installment principal only (TENOR * monthlyAmount),
  // matching totalCapitalPaid (Σ PAID+ADVANCED rows, booking excluded per §6.3 Q9).
  // Q2: Total plan adds booking amount (10,000) for overall plan commitment display;
  // remaining balance stays installments-only.
  const bookingAmount = sub.data?.bookingAmount ? Number(sub.data.bookingAmount) : 10000;
  const totalInstallmentCommitment = TENOR * monthlyAmount;
  const totalPlanCommitment = totalInstallmentCommitment + (hasMonthlyAmount ? bookingAmount : 0);

  const wallet = resolveWallet(stats.data);
  const canProcess = wallet.processingSupported && wallet.availableCommission > 0;

  const advanceBalance = stats.data?.advanceBalance ?? sub.data?.advanceBalance ?? 0;
  // Owner rule: the card shows what is prepaid (ADVANCED rows + remainder); older API → advanceBalance (see resolvePrepaid).
  const prepaid = resolvePrepaid({
    advanceBalance,
    prepaidAmount: stats.data?.prepaidAmount ?? sub.data?.prepaidAmount,
    prepaidMonths: stats.data?.prepaidMonths ?? sub.data?.prepaidMonths,
  });

  // BD-31: agents (L2–L6) never own a subscription, so they always get the agency view and never an arrears state.
  const delinquencyStatus = isAgent
    ? DelinquencyStatus.ACTIVE
    : (stats.data?.delinquencyStatus ?? sub.data?.delinquencyStatus ?? DelinquencyStatus.ACTIVE);
  const hasNoPlot = isAgent;
  const plotDisplayTitle = user?.apartmentId ?? sub.data?.userId ?? 'Sristi Green Valley Ltd';

  return (
    <div className="dashboard-root" data-testid="dashboard-page">
      {/* 1. Header & Identity Bar */}
      <div className="dash-header">
        <div className="dash-header-left">
          <div className="dash-greeting-row">
            <h1 className="dash-title">
              {t('dashboard.welcome')}, {user?.fullName ?? ''}
            </h1>
            <span
              className={`designation-pill ${
                isDirector ? 'director' : isAgent ? 'agency' : 'investor'
              }`}
            >
              {isDirector ? '🏛️' : isAgent ? '💼' : '🏡'} {TIER_DESIGNATION_MAP[userTier] ?? userTier}
            </span>
          </div>
          <div className="dash-meta-row">
            <span>
              Account ID: <strong className="dash-account-id">{user?.userId ?? '260101000001'}</strong>
            </span>
            <span>•</span>
            <span
              className={`dash-status-dot ${
                delinquencyStatus === DelinquencyStatus.ACTIVE
                  ? 'active'
                  : delinquencyStatus === DelinquencyStatus.IN_NOTICE_PERIOD || delinquencyStatus === DelinquencyStatus.DUE
                  ? 'warning'
                  : 'danger'
              }`}
            >
              <span className="dot-indicator" />
              {delinquencyStatus === DelinquencyStatus.ACTIVE
                ? 'Active Account (Good Standing)'
                : delinquencyStatus === DelinquencyStatus.IN_NOTICE_PERIOD || delinquencyStatus === DelinquencyStatus.DUE
                ? 'Notice: Arrears Pending'
                : 'Account Restricted'}
            </span>
          </div>
        </div>
      </div>

      <QueryState
        loading={stats.isPending}
        error={stats.error}
        onRetry={() => void stats.refetch()}
        skeletonCount={4}
      />

      {stats.data && (
        <>
          {/* 2. Hero Real Estate Asset OR Agency Operations Banner */}
          {hasNoPlot ? (
            <div className="hero-property-card" data-testid="hero-agency-card">
              <div className="hero-grid">
                <div className="hero-plot-details">
                  <div className="hero-project-eyebrow">
                    <Building2 size={15} />
                    Sristi Green Valley Ltd • Field Marketing Agency
                  </div>
                  <h2 className="hero-plot-title">
                    {TIER_DESIGNATION_MAP[userTier] ?? userTier} Portfolio
                  </h2>
                  <div className="hero-plot-tags">
                    <span className="hero-tag">
                      <Users size={12} /> Downline: {stats.data.totalDownlineCount ?? 0} Executives
                    </span>
                    <span className="hero-tag">
                      <Wallet size={12} /> Earned Commission Pool
                    </span>
                    <span className="hero-tag">
                      <Sparkles size={12} /> Active Agency License
                    </span>
                  </div>
                  <div className="hero-advisor-note">
                    Executive Division Leader:{' '}
                    <strong>{user?.fullName ?? 'Field Executive'}</strong>
                  </div>
                </div>

                <div className="hero-equity-panel">
                  <div className="equity-figures-row">
                    <div className="equity-figure-block">
                      <span className="equity-figure-label">{t('dashboard.availableForWithdrawal')}</span>
                      <span className="equity-figure-value highlight" data-testid="hero-available-for-withdrawal">
                        ৳ {wallet.availableForWithdrawal.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="equity-figure-block">
                      <span className="equity-figure-label">Downline Network</span>
                      <span className="equity-figure-value">
                        {stats.data.totalDownlineCount ?? 0} Members
                      </span>
                    </div>
                    <div className="equity-figure-block" style={{ textAlign: 'right' }}>
                      <span className="equity-figure-label">Account Standing</span>
                      <span className="equity-figure-value">Good Standing</span>
                    </div>
                  </div>
                  <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                    <Link to="/team">
                      <Button variant="primary" size="sm">
                        {t('nav.team')} →
                      </Button>
                    </Link>
                    <Link to="/commissions">
                      <Button variant="secondary" size="sm">
                        {t('nav.commissions')} →
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="hero-property-card">
            <div className="hero-grid">
              <div className="hero-plot-details">
                <div className="hero-project-eyebrow">
                  <Building2 size={15} />
                  Sristi Green Valley Ltd
                </div>
                <h2 className="hero-plot-title">{plotDisplayTitle}</h2>
                <div className="hero-plot-tags">
                  <span className="hero-tag">
                    <Calendar size={12} /> {t('dashboard.tenorPlan')}
                  </span>
                </div>
              </div>

              <div className="hero-equity-panel">
                <div className="equity-figures-row">
                  <div className="equity-figure-block">
                    <span className="equity-figure-label">{t('dashboard.capitalInvested')}</span>
                    <span className="equity-figure-value highlight">
                      ৳ {totalCapitalPaid.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="equity-figure-block">
                    <span className="equity-figure-label">{t('dashboard.totalPlanCommitmentLabel')}</span>
                    <span className="equity-figure-value">
                      ৳ {totalInstallmentCommitment.toLocaleString('en-IN')}
                    </span>
                    <span className="equity-figure-sub" style={{ fontSize: '0.6875rem', color: '#a3cfbb', marginTop: 2 }} data-testid="total-plan-line">
                      Total plan: ৳ {totalPlanCommitment.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="equity-figure-block" style={{ textAlign: 'right' }}>
                    <span className="equity-figure-label">{t('dashboard.progress')}</span>
                    <span className="equity-figure-value">
                      {clearedCount} / {totalCount} Mo ({progressPercent}%)
                    </span>
                  </div>
                </div>

                <div className="milestone-track-wrap" data-testid="dashboard-milestones">
                  <div className="milestone-bar-bg">
                    <div
                      className="milestone-bar-fill"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="milestone-markers">
                    <span
                      className={`milestone-node ${clearedCount >= 12 ? 'achieved' : ''}`}
                      data-testid="milestone-node-12"
                    >
                      ● {t('milestones.m12')}
                    </span>
                    <span
                      className={`milestone-node ${clearedCount >= 84 ? 'achieved' : ''}`}
                      data-testid="milestone-node-84"
                    >
                      ● {t('milestones.m84')}
                    </span>
                    <span
                      className={`milestone-node ${clearedCount >= 120 ? 'achieved' : ''}`}
                      data-testid="milestone-node-120"
                    >
                      ● {t('milestones.m120')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

          {/* 3. Balanced 4-Column KPI Grid with Canonical Actions */}
          <div className="dash-kpi-grid">
            {/* Subscribers only: agents have no installments or payment options. */}
            {!isAgent && (
              <>
            {/* KPI 1: Next Due Installment (EMI) */}
            <Card>
              <div className="kpi-card-inner">
                <div className="kpi-top">
                  <div className="kpi-head-row">
                    <span className="kpi-icon-label">
                      <span className="kpi-icon-wrap">
                        <CreditCard size={14} />
                      </span>
                      {t('dashboard.nextDue')}
                    </span>
                    {stats.data.nextDueStatus && (
                      <Badge tone={STATUS_TONE[stats.data.nextDueStatus] ?? 'neutral'}>
                        {stats.data.nextDueStatus}
                      </Badge>
                    )}
                  </div>
                  <div className="kpi-val">
                    {hasMonthlyAmount ? (
                      <AmountDisplay amount={monthlyAmount} />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </div>
                  <div className="kpi-sub">
                    Due date:{' '}
                    <strong>
                      {formatDateTime(stats.data.nextDueDate, locale) || 'All Cleared'}
                    </strong>
                  </div>
                </div>
                <div className="kpi-footer-action">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                    {BKASH_ENABLED && (
                      <PayLink to="/payments/bkash" className="kpi-cta-primary">
                        <Button variant="primary" size="sm" style={{ width: '100%' }}>
                          ⚡ {t('payments.payWithBkash')}
                        </Button>
                      </PayLink>
                    )}
                    <PayLink
                      to="/payments/bank-slip/upload"
                      className="kpi-link"
                      data-testid="qa-upload"
                      style={{ fontSize: '0.71875rem', justifyContent: 'center' }}
                    >
                      {t('dashboard.orSubmitBankSlip')}
                    </PayLink>
                  </div>
                </div>
              </div>
            </Card>

            {/* KPI 2: Advance Capital Balance */}
            <Card>
              <div className="kpi-card-inner">
                <div className="kpi-top">
                  <div className="kpi-head-row">
                    <span className="kpi-icon-label">
                      <span className="kpi-icon-wrap">
                        <Wallet size={14} />
                      </span>
                      {t('dashboard.advance')}
                    </span>
                    {prepaid.amount > 0 ? (
                      <Badge tone="info">
                        {prepaid.months > 0 ? t('installments.prepaidBadge') : t('installments.partialCreditBadge')}
                      </Badge>
                    ) : (
                      <Badge tone="neutral">{t('installments.zeroCreditBadge')}</Badge>
                    )}
                  </div>
                  <div className="kpi-val">
                    <AmountDisplay amount={prepaid.amount} />
                  </div>
                  <div className="kpi-sub">
                    {prepaid.months > 0 && (
                      <span data-testid="dashboard-prepaid-months">
                        {t(prepaid.months === 1 ? 'installments.monthPrepaid' : 'installments.monthsPrepaid', {
                          months: formatNumber(prepaid.months, locale),
                        })}
                      </span>
                    )}
                    {prepaid.months > 0 && prepaid.leftover > 0 && (
                      <span data-testid="dashboard-credit-note" style={{ display: 'block', fontSize: '0.85em', opacity: 0.8 }}>
                        {t('installments.leftoverCredit', { credit: formatBDT(prepaid.leftover, locale) })}
                      </span>
                    )}
                    {prepaid.months === 0 &&
                      (prepaid.amount > 0
                        ? t('installments.advanceSubWithCredit')
                        : t('installments.advanceSubNoCredit'))}
                  </div>
                </div>
                <div className="kpi-footer-action">
                  <PayLink to="/payments/bank-slip/upload" className="kpi-link">
                    {t('installments.addAdvanceDepositLink')}
                  </PayLink>
                </div>
              </div>
            </Card>

              </>
            )}

            {/* If regular investor (!isAgent), show investment metrics with zero agency/commission mention */}
            {!isAgent ? (
              <>
                {/* KPI 3: Installment Progress */}
                <Card>
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <Receipt size={14} />
                          </span>
                          {t('dashboard.installmentProgress')}
                        </span>
                        <Badge tone="success">
                          {clearedCount} / {totalCount} Mo
                        </Badge>
                      </div>
                      <div className="kpi-val">
                        ৳ {totalCapitalPaid.toLocaleString('en-IN')}
                      </div>
                      <div className="kpi-sub">
                        {t('dashboard.capitalPaidSub', { clearedCount })}
                      </div>
                    </div>
                    <div className="kpi-footer-action">
                      <Link to="/installments" className="kpi-link">
                        {t('dashboard.viewMilestones')} →
                      </Link>
                    </div>
                  </div>
                </Card>

                {/* KPI 4: Tenure Milestones (Q7 amendment) */}
                <Card data-testid="tenure-milestones-card">
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <Building2 size={14} />
                          </span>
                          {t('dashboard.tenureMilestones')}
                        </span>
                        <Badge tone={clearedCount >= 120 ? 'success' : 'info'}>
                          {clearedCount >= 120 ? t('milestones.completed') : t('milestones.nextMilestone')}
                        </Badge>
                      </div>
                      <div className="kpi-val">
                        {clearedCount < 12
                          ? 'Month 12'
                          : clearedCount < 84
                          ? 'Month 84'
                          : 'Month 120'}
                      </div>
                      <div className="kpi-sub">
                        {clearedCount < 12
                          ? t('milestones.m12Desc', { remaining: 12 - clearedCount })
                          : clearedCount < 84
                          ? t('milestones.m84Desc', { remaining: 84 - clearedCount })
                          : clearedCount < 120
                          ? t('milestones.m120Desc', { remaining: 120 - clearedCount })
                          : t('milestones.allCompleted')}
                      </div>
                    </div>
                    <div className="kpi-footer-action">
                      <Link to="/installments" className="kpi-link">
                        {t('dashboard.viewMilestones')} →
                      </Link>
                    </div>
                  </div>
                </Card>
              </>
            ) : (
              <>
                {/* Wallet: Total earned (lifetime) -> Available commission (unprocessed) -> Available for withdrawal */}
                <Card data-testid="total-earned-card">
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <TrendingUp size={14} />
                          </span>
                          {t('dashboard.totalCommissionEarned')}
                        </span>
                        <Badge tone="accent">Agency</Badge>
                      </div>
                      <div className="kpi-val" data-testid="total-earned-value">
                        <AmountDisplay amount={wallet.totalEarned} />
                      </div>
                      <div className="kpi-sub">{t('dashboard.totalCommissionEarnedSub')}</div>
                    </div>
                  </div>
                </Card>

                <Card data-testid="available-commission-card">
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <Sparkles size={14} />
                          </span>
                          {t('dashboard.availableCommission')}
                        </span>
                      </div>
                      <div className="kpi-val" data-testid="available-commission-value">
                        <AmountDisplay amount={wallet.availableCommission} />
                      </div>
                      <div className="kpi-sub">{t('dashboard.availableCommissionSub')}</div>
                    </div>
                    {wallet.processingSupported && (
                      <div className="kpi-footer-action">
                        <Button
                          variant="primary"
                          size="sm"
                          style={{ width: '100%' }}
                          disabled={!canProcess}
                          onClick={() => setProcessOpen(true)}
                          data-testid="process-withdrawal-btn"
                        >
                          {t('dashboard.processForWithdrawal')}
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>

                <Card data-testid="commission-card">
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <Wallet size={14} />
                          </span>
                          {t('dashboard.availableForWithdrawal')}
                        </span>
                      </div>
                      <div className="kpi-val" data-testid="available-for-withdrawal-value">
                        <AmountDisplay amount={wallet.availableForWithdrawal} />
                      </div>
                      <div className="kpi-sub">{t('dashboard.availableForWithdrawalSub')}</div>
                    </div>
                    <div className="kpi-footer-action">
                      <Link to="/withdrawals" style={{ width: '100%' }}>
                        <Button variant="secondary" size="sm" data-testid="qa-withdraw" style={{ width: '100%' }}>
                          {t('dashboard.withdrawFunds')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>

                {/* KPI 4: Downline Team (L3+) */}
                <Card data-testid="downline-card">
                  <div className="kpi-card-inner">
                    <div className="kpi-top">
                      <div className="kpi-head-row">
                        <span className="kpi-icon-label">
                          <span className="kpi-icon-wrap">
                            <Users size={14} />
                          </span>
                          {t('dashboard.team')}
                        </span>
                        {stats.data.totalDownlineCount !== null ? (
                          <Badge tone="success">Network</Badge>
                        ) : (
                          <Badge tone="neutral">MM and above</Badge>
                        )}
                      </div>
                      <div className="kpi-val">
                        {stats.data.totalDownlineCount === null ? (
                          <span className="muted" style={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                            — {t('dashboard.l3Only')}
                          </span>
                        ) : (
                          `${stats.data.totalDownlineCount} Members`
                        )}
                      </div>
                      <div className="kpi-sub">
                        {stats.data.totalDownlineCount !== null
                          ? 'Total active agents & subscribers across your sales tree.'
                          : 'Multi-tier agency analytics are unlocked for Marketing Managers (MM) and above.'}
                      </div>
                    </div>
                    <div className="kpi-footer-action">
                      {stats.data.totalDownlineCount !== null ? (
                        <Link to="/team" className="kpi-link">
                          View Team Tree & Sales →
                        </Link>
                      ) : (
                        <Link to="/notices" className="kpi-link">
                          Township Site Updates →
                        </Link>
                      )}
                    </div>
                  </div>
                </Card>
              </>
            )}
          </div>

          {/* 4. Split 2-Column Feed: Recent Payments & Official Notices */}
          <div className="dash-split-grid">
            {/* Recent Payments Table: subscribers only */}
            {!isAgent && (
            <Card>
              <div className="dash-panel-header">
                <span className="dash-panel-title">
                  <Clock size={16} />
                  {t('dashboard.recent')}
                </span>
                <Link to="/payments" className="dash-panel-link">
                  View All Payments <ArrowRight size={13} style={{ display: 'inline' }} />
                </Link>
              </div>

              {(stats.data.recentPayments ?? []).length === 0 ? (
                <p className="muted" data-testid="recent-empty">
                  {t('payments.noPayments')}
                </p>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Date & Time</th>
                        <th>Channel</th>
                        <th>Amount</th>
                        <th style={{ textAlign: 'right' }}>Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(stats.data.recentPayments ?? []).map((p) => {
                        const channelInfo = formatChannelName(p.channel);
                        return (
                          <tr key={p.id}>
                            <td style={{ fontWeight: 500 }}>
                              {formatDateTime(p.createdAt, locale)}
                            </td>
                            <td>
                              <span className={`channel-pill ${channelInfo.cls}`}>
                                {channelInfo.label}
                              </span>
                            </td>
                            <td style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                              <AmountDisplay amount={p.amount} />
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <Link
                                to={`/payments/${p.id}/invoice`}
                                className="receipt-action-btn"
                              >
                                <FileText size={11} />
                                Receipt
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            )}

            {/* Latest Official Notice */}
            <Card>
              <div className="dash-panel-header">
                <span className="dash-panel-title">
                  <Bell size={16} />
                  {t('dashboard.latestNotice')}
                </span>
                <Link to="/notices" className="dash-panel-link" data-testid="qa-notices">
                  Notice Board <ArrowRight size={13} style={{ display: 'inline' }} />
                </Link>
              </div>

              {stats.data.latestNotice ? (
                <div className="notice-box" data-testid="latest-notice">
                  <div className="notice-box-head">
                    <Badge tone="info">{stats.data.latestNotice.category}</Badge>
                    <span className="muted" style={{ fontSize: '0.71875rem' }}>
                      {formatDateTime(stats.data.latestNotice.publishedAt, locale)}
                    </span>
                  </div>
                  <h3 className="notice-title">{stats.data.latestNotice.title}</h3>
                  <p className="notice-body">{stats.data.latestNotice.body}</p>
                  <div className="notice-footer">
                    <span>Issued by Sristi Green Valley Ltd</span>
                  </div>
                </div>
              ) : (
                <p className="muted">{t('notices.empty')}</p>
              )}
            </Card>
          </div>
          {isAgent && (
            <ProcessWithdrawalModal
              open={processOpen}
              onClose={() => setProcessOpen(false)}
              availableCommission={wallet.availableCommission}
              availableForWithdrawal={wallet.availableForWithdrawal}
            />
          )}
        </>
      )}
    </div>
  );
}
